<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, minValue } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  broadcast: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const isEditing = computed(() => !!props.broadcast);

// This dialog is v-if-gated by the parent (only mounted while shown), unlike
// ConfirmDeleteCampaignDialog which stays mounted and is opened imperatively — so it has to
// open itself as soon as it mounts, via the native <dialog> element's showModal().
const dialogRef = ref(null);
onMounted(() => {
  dialogRef.value?.open();
  store.dispatch('attributes/get');
});

const DAYS = [0, 1, 2, 3, 4, 5, 6]; // Sun..Sat, matches Ruby Time#wday

const inboxes = useMapGetter('inboxes/getWhatsappLiteInboxes');
const labels = useMapGetter('labels/getLabels');
const kanbans = useMapGetter('kanbans/getKanbans');
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const conversationAttributes = useMapGetter(
  'attributes/getConversationAttributes'
);
const uiFlags = useMapGetter('evolutionBroadcasts/getUIFlags');

const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const minutesToTime = minutes => {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  const m = (minutes % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
};

const timeToMinutes = time => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const buildInitialState = broadcast => ({
  title: broadcast?.title || '',
  message: broadcast?.message || '',
  inboxId: broadcast?.inbox?.id || null,
  allContacts: broadcast?.audience_filters?.all_contacts || false,
  labelIds: broadcast?.audience_filters?.label_ids || [],
  kanbanStageIds: broadcast?.audience_filters?.kanban_stage_ids || [],
  dailyLimit: broadcast?.daily_limit || 50,
  daysOfWeek: broadcast?.days_of_week || [1, 2, 3, 4, 5],
  windowStart: minutesToTime(broadcast?.window_start_minutes ?? 480),
  windowEnd: minutesToTime(broadcast?.window_end_minutes ?? 1080),
  media: null,
});

const state = reactive(buildInitialState(props.broadcast));

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  dailyLimit: { required, minValue: minValue(1) },
  daysOfWeek: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(rules, state);

const audienceIsValid = computed(
  () =>
    state.allContacts ||
    state.labelIds.length > 0 ||
    state.kanbanStageIds.length > 0
);

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !audienceIsValid.value
);

const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);
const labelOptions = computed(() =>
  labels.value.map(label => ({ value: label.id, label: label.title }))
);
const kanbanStageOptions = computed(() =>
  kanbans.value.flatMap(kanban =>
    (kanban.kanban_stages || []).map(stage => ({
      value: stage.id,
      label: `${kanban.name} / ${stage.name}`,
    }))
  )
);

const messageTextAreaRef = ref(null);
const showVariableMenu = ref(false);
const toggleVariableMenu = (value = !showVariableMenu.value) => {
  showVariableMenu.value = value;
};

const variableMenuSections = computed(() => {
  const contactItems = [
    {
      label: t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.VARIABLES.CONTACT_NAME'),
      value: '{{contact.name}}',
    },
    {
      label: t(
        'CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.VARIABLES.CONTACT_FIRST_NAME'
      ),
      value: '{{contact.first_name}}',
    },
    {
      label: t(
        'CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.VARIABLES.CONTACT_LAST_NAME'
      ),
      value: '{{contact.last_name}}',
    },
    {
      label: t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.VARIABLES.CONTACT_PHONE'),
      value: '{{contact.phone_number}}',
    },
    ...contactAttributes.value.map(attribute => ({
      label: attribute.attributeDisplayName,
      value: `{{contact.custom_attribute.${attribute.attributeKey}}}`,
    })),
  ];

  const conversationItems = [
    {
      label: t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.VARIABLES.CONVERSATION_ID'),
      value: '{{conversation.id}}',
    },
    ...conversationAttributes.value.map(attribute => ({
      label: attribute.attributeDisplayName,
      value: `{{conversation.custom_attribute.${attribute.attributeKey}}}`,
    })),
  ];

  return [
    {
      title: t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.VARIABLES.CONTACT_SECTION'),
      items: contactItems.map(item => ({ ...item, action: item.value })),
    },
    {
      title: t(
        'CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.VARIABLES.CONVERSATION_SECTION'
      ),
      items: conversationItems.map(item => ({ ...item, action: item.value })),
    },
  ];
});

// Inserts at the caret when the textarea is focused (tracked selection), otherwise appends —
// keeps this usable even right after opening the dialog, before the user has clicked in.
// DropdownMenu's @action emits { action, value } — the snippet lives in `value`.
const insertVariable = ({ value: snippet }) => {
  toggleVariableMenu(false);
  const textarea = messageTextAreaRef.value?.textareaRef;
  const start = textarea?.selectionStart ?? state.message.length;
  const end = textarea?.selectionEnd ?? state.message.length;
  state.message =
    state.message.slice(0, start) + snippet + state.message.slice(end);

  nextTick(() => {
    if (!textarea) return;
    const caret = start + snippet.length;
    textarea.focus();
    textarea.setSelectionRange(caret, caret);
  });
};

const dayLabel = day =>
  t(`CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.DAYS_OF_WEEK.DAYS.${day}`);

const toggleDay = day => {
  const idx = state.daysOfWeek.indexOf(day);
  if (idx === -1) {
    state.daysOfWeek.push(day);
  } else if (state.daysOfWeek.length > 1) {
    state.daysOfWeek.splice(idx, 1);
  }
};

const onMediaChange = event => {
  const [file] = event.target.files || [];
  state.media = file || null;
};

const removeMedia = () => {
  state.media = null;
};

const existingMediaUrl = computed(() =>
  !state.media && props.broadcast?.media_url ? props.broadcast.media_url : null
);

watch(
  () => props.broadcast,
  broadcast => Object.assign(state, buildInitialState(broadcast)),
  { deep: true }
);

const preparePayload = () => {
  const payload = {
    title: state.title,
    message: state.message,
    inbox_id: state.inboxId,
    daily_limit: state.dailyLimit,
    days_of_week: state.daysOfWeek,
    window_start_minutes: timeToMinutes(state.windowStart),
    window_end_minutes: timeToMinutes(state.windowEnd),
    audience_filters: {
      all_contacts: state.allContacts,
      label_ids: state.allContacts ? [] : state.labelIds,
      kanban_stage_ids: state.allContacts ? [] : state.kanbanStageIds,
    },
  };
  if (state.media) payload.media = state.media;
  return payload;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !audienceIsValid.value) return;

  try {
    if (isEditing.value) {
      await store.dispatch('evolutionBroadcasts/update', {
        id: props.broadcast.id,
        ...preparePayload(),
      });
      useAlert(
        t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.API.UPDATE_SUCCESS_MESSAGE')
      );
    } else {
      await store.dispatch('evolutionBroadcasts/create', preparePayload());
      useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
        type: 'whatsapp_lite_broadcast',
      });
      useAlert(t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.API.SUCCESS_MESSAGE'));
    }
    emit('close');
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.API.ERROR_MESSAGE'));
  }
};

const handleClose = () => emit('close');
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      isEditing
        ? t('CAMPAIGN.WHATSAPP_LITE.EDIT.TITLE')
        : t('CAMPAIGN.WHATSAPP_LITE.CREATE.TITLE')
    "
    :confirm-button-label="
      t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.BUTTONS.CREATE')
    "
    :cancel-button-label="
      t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.BUTTONS.CANCEL')
    "
    :disable-confirm-button="isSubmitDisabled"
    :is-loading="isSaving"
    @confirm="handleSubmit"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="state.title"
          :label="t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.TITLE.LABEL')"
          :placeholder="
            t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.TITLE.PLACEHOLDER')
          "
        />

        <div class="flex flex-col gap-1">
          <label
            for="wl-inbox"
            class="mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.INBOX.LABEL') }}
          </label>
          <ComboBox
            id="wl-inbox"
            v-model="state.inboxId"
            :options="inboxOptions"
            :placeholder="
              t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.INBOX.PLACEHOLDER')
            "
          />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.MESSAGE.LABEL') }}
          </label>
          <div
            v-on-clickaway="() => toggleVariableMenu(false)"
            class="relative"
          >
            <Button
              variant="faded"
              color="slate"
              size="xs"
              icon="i-lucide-braces"
              :label="
                t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.VARIABLES.INSERT_BUTTON')
              "
              @click="toggleVariableMenu()"
            />
            <DropdownMenu
              v-if="showVariableMenu"
              :menu-sections="variableMenuSections"
              class="mt-1 ltr:right-0 rtl:left-0 top-full w-64"
              @action="insertVariable($event)"
            />
          </div>
        </div>
        <TextArea
          ref="messageTextAreaRef"
          v-model="state.message"
          :placeholder="
            t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.MESSAGE.PLACEHOLDER')
          "
          :max-length="4096"
          show-character-count
        />
        <p class="text-xs text-n-slate-11 whitespace-pre-line">
          {{ t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.MESSAGE.HELP') }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <label
          for="wl-media"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.MEDIA.LABEL') }}
        </label>
        <input
          id="wl-media"
          type="file"
          accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx"
          class="text-sm text-n-slate-11 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-n-ruby-6 file:text-sm file:font-medium file:bg-n-solid-1 file:text-n-ruby-11 hover:file:bg-n-ruby-2"
          @change="onMediaChange"
        />
        <div
          v-if="state.media || existingMediaUrl"
          class="flex items-center gap-2 text-xs text-n-slate-11"
        >
          <span>{{
            state.media
              ? state.media.name
              : t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.MEDIA.CURRENT')
          }}</span>
          <button
            type="button"
            class="text-n-ruby-11 hover:underline"
            @click="removeMedia"
          >
            {{ t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.MEDIA.REMOVE') }}
          </button>
        </div>
      </div>

      <div
        class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak bg-n-slate-2 dark:bg-n-solid-2"
      >
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{
              t(
                'CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.AUDIENCE.ALL_CONTACTS_LABEL'
              )
            }}
          </label>
          <Switch v-model="state.allContacts" />
        </div>
        <template v-if="!state.allContacts">
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{
                t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.AUDIENCE.LABELS_LABEL')
              }}
            </label>
            <TagMultiSelectComboBox
              v-model="state.labelIds"
              :options="labelOptions"
              :placeholder="
                t(
                  'CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.AUDIENCE.LABELS_PLACEHOLDER'
                )
              "
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{
                t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.AUDIENCE.KANBAN_LABEL')
              }}
            </label>
            <TagMultiSelectComboBox
              v-model="state.kanbanStageIds"
              :options="kanbanStageOptions"
              :placeholder="
                t(
                  'CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.AUDIENCE.KANBAN_PLACEHOLDER'
                )
              "
            />
          </div>
          <p v-if="!audienceIsValid" class="text-xs text-n-ruby-11">
            {{ t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.AUDIENCE.ERROR') }}
          </p>
        </template>
      </div>

      <div class="grid grid-cols-3 gap-4">
        <Input
          v-model="state.dailyLimit"
          type="number"
          min="1"
          :label="t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.DAILY_LIMIT.LABEL')"
        />
        <Input
          v-model="state.windowStart"
          type="time"
          :label="t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.WINDOW.START_LABEL')"
        />
        <Input
          v-model="state.windowEnd"
          type="time"
          :label="t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.WINDOW.END_LABEL')"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP_LITE.CREATE.FORM.DAYS_OF_WEEK.LABEL') }}
        </label>
        <div class="flex flex-wrap items-center gap-2">
          <button
            v-for="day in DAYS"
            :key="day"
            type="button"
            :title="dayLabel(day)"
            class="flex items-center justify-center text-sm font-medium transition-colors rounded-full size-10 shrink-0"
            :class="
              state.daysOfWeek.includes(day)
                ? 'bg-n-ruby-9 text-white'
                : 'border border-n-weak text-n-slate-11 hover:bg-n-slate-2 dark:hover:bg-n-solid-2'
            "
            @click="toggleDay(day)"
          >
            {{ dayLabel(day).charAt(0) }}
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>
